import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function QuoteRequestModal({ 
  isOpen, 
  onClose, 
  product, 
  selectedType, 
  quantity,
  API 
}) {
  const { i18n } = useTranslation();
  const { user } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    user_name: '',
    user_email: '',
    user_phone: '',
    message: ''
  });

  // Pré-remplir avec les infos du compte si l'utilisateur est connecté
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        user_name: prev.user_name || user.name || '',
        user_email: prev.user_email || user.email || '',
      }));
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const quoteData = {
        product_id: product.id,
        product_name: i18n.language === 'fr' ? product.name_fr : product.name_en,
        user_name: formData.user_name,
        user_email: formData.user_email,
        user_phone: formData.user_phone || null,
        message: formData.message || null,
        quantity: quantity,
        product_type: selectedType
      };

      const userIdParam = user ? `?user_id=${user.id}` : '';
      await axios.post(`${API}/quotes/request${userIdParam}`, quoteData);
      
      toast.success(
        i18n.language === 'fr' 
          ? 'Votre demande de devis a été envoyée avec succès ! Nous vous contacterons bientôt.' 
          : 'Your quote request has been sent successfully! We will contact you soon.'
      );
      
      // Reset form
      setFormData({
        user_name: '',
        user_email: '',
        user_phone: '',
        message: ''
      });
      
      onClose();
    } catch (error) {
      console.error('Error submitting quote request:', error);
      toast.error(
        i18n.language === 'fr' 
          ? 'Erreur lors de l\'envoi de la demande. Veuillez réessayer.' 
          : 'Error sending request. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {i18n.language === 'fr' ? 'Demander un devis' : 'Request a Quote'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Product Info */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-sm font-semibold text-gray-700">
              {i18n.language === 'fr' ? product.name_fr : product.name_en}
            </p>
            <p className="text-xs text-gray-500">
              {i18n.language === 'fr' ? 'Quantité' : 'Quantity'}: {quantity} • {
                selectedType === 'new' ? (i18n.language === 'fr' ? 'Neuf' : 'New') :
                selectedType === 'used' ? (i18n.language === 'fr' ? 'Occasion' : 'Used') :
                (i18n.language === 'fr' ? 'Location' : 'Rental')
              }
            </p>
          </div>

          {/* User Name */}
          <div>
            <Label htmlFor="user_name">
              {i18n.language === 'fr' ? 'Nom complet' : 'Full Name'} *
            </Label>
            <Input
              id="user_name"
              type="text"
              required
              value={formData.user_name}
              onChange={(e) => setFormData({...formData, user_name: e.target.value})}
              placeholder={i18n.language === 'fr' ? 'Votre nom' : 'Your name'}
            />
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="user_email">
              {i18n.language === 'fr' ? 'Email' : 'Email'} *
            </Label>
            <Input
              id="user_email"
              type="email"
              required
              value={formData.user_email}
              onChange={(e) => setFormData({...formData, user_email: e.target.value})}
              placeholder={i18n.language === 'fr' ? 'votre@email.com' : 'your@email.com'}
            />
          </div>

          {/* Phone */}
          <div>
            <Label htmlFor="user_phone">
              {i18n.language === 'fr' ? 'Téléphone (optionnel)' : 'Phone (optional)'}
            </Label>
            <Input
              id="user_phone"
              type="tel"
              value={formData.user_phone}
              onChange={(e) => setFormData({...formData, user_phone: e.target.value})}
              placeholder={i18n.language === 'fr' ? '+33 6 12 34 56 78' : '+1 234 567 8900'}
            />
          </div>

          {/* Message */}
          <div>
            <Label htmlFor="message">
              {i18n.language === 'fr' ? 'Message (optionnel)' : 'Message (optional)'}
            </Label>
            <textarea
              id="message"
              rows={4}
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              placeholder={i18n.language === 'fr' 
                ? 'Détails supplémentaires, questions, délais souhaités...' 
                : 'Additional details, questions, desired timeframe...'}
              className="w-full px-3 py-2 border rounded-md resize-none"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              disabled={loading}
            >
              {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              type="submit" 
              className="bg-[#635BFF] hover:bg-[#5248d9]"
              disabled={loading}
            >
              {loading ? (
                i18n.language === 'fr' ? 'Envoi...' : 'Sending...'
              ) : (
                i18n.language === 'fr' ? 'Envoyer la demande' : 'Send Request'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
